import { useEffect, useState } from "react";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@material-ui/core/Button";
import AddCircle from "@material-ui/icons/AddCircle";
import Dialog from "@material-ui/core/Dialog";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import Typography from "@material-ui/core/Typography";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { schema } from "./schema";
import { useGuicheFormStyles } from "./styles";
import { useAttendanceLocalStyles } from "../../../../../styles";
import { AttendanceLocalGuicheCreateData } from "../../../../../../../services/api-tansquery/attendance-local/guiche/create";
import { AttendanceLocalGuicheUpdateData } from "../../../../../../../services/api-tansquery/attendance-local/guiche/update";
import { AttendanceLocalGuiche } from "../../../../../../../@types/attendance-local-guiche";
import { UseMutationResult } from "@tanstack/react-query";
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";
import UsersField from "./components/users-field";
import Grid from "@material-ui/core/Grid";

type ServiceFormProps = {
    slug: string;
    guiche?: AttendanceLocalGuiche;
    buttonTrigger?: React.ReactNode;
    mutation: UseMutationResult<
        AttendanceLocalGuiche,
        unknown,
        AttendanceLocalGuicheCreateData | AttendanceLocalGuicheUpdateData,
        unknown
    >
};

export default function GuicheForm({ slug, guiche, buttonTrigger, mutation }: ServiceFormProps) {
    const [open, setOpen] = useState(false);
    const { classes } = useGuicheFormStyles();
    const { classes: defaultClasses } = useAttendanceLocalStyles();
    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            ordem: "",
            tipo: "Consulta",
            unidade: slug || "",
            ativo: true,
            users: [],
        }
    });

    async function handleSubmit(data: z.infer<typeof schema>) {
        if (mutation.isLoading) {
            return;
        }

        await mutation.mutateAsync({
            unidade: slug,
            ordem: Number(data.ordem),
            tipo: data.tipo,
            ativo: data.ativo,
            users: data.users,
        });
    }

    useEffect(() => {
        if (open && guiche) {
            form.reset({
                ordem: guiche.ordem.toString(),
                tipo: guiche.tipo,
                unidade: guiche.unidade,
                ativo: guiche.ativo,
                users: guiche.user.map(user => user.username) || [],
            });
        } else {
            form.reset();
        }
    }, [open, guiche, form]);

    useEffect(() => {
        if (mutation.isSuccess) {
            setOpen(false);
        }
    }, [mutation.isSuccess]);

    return (
        <>
            {buttonTrigger ? (
                <span onClick={() => setOpen(true)}>
                    {buttonTrigger}
                </span>
            ) : (
                <Button
                    startIcon={<AddCircle />}
                    className={defaultClasses.addButton}
                    onClick={() => setOpen(true)}
                >
                    Guichê de Recepção
                </Button>
            )}
            <Dialog open={open}>
                <Card
                    component="form"
                    onSubmit={form.handleSubmit(handleSubmit)}
                >
                    <CardHeader
                        avatar={<AddCircle />}
                        className={classes.cardHeader}
                        title={<Typography variant="h5">Guichê de Recepção</Typography>}
                    />
                    <CardContent
                        container
                        spacing={2}
                        component={Grid}
                    >
                        <Grid item xs={3}>
                            <Controller
                                name="ordem"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        required
                                        fullWidth
                                        label="Ordem"
                                        type="number"
                                        variant="outlined"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message || "Informe a ordem"}
                                        style={{ flex: 1 }}
                                        inputProps={{
                                            min: 1,
                                        }}
                                        InputProps={{
                                            required: false,
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={9}>
                            <Controller
                                name="users"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <UsersField
                                        slug={slug}
                                        field={field}
                                        fieldState={fieldState}
                                        users={guiche?.user}
                                        style={{ flex: 3 }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} style={{ paddingLeft: 10 }}>
                            <Controller
                                name="ativo"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <FormControl error={!!fieldState.error}>
                                        <FormControlLabel
                                            label="Este guichê está ativo?"
                                            control={<Switch {...field} checked={field.value} />}
                                        />
                                        <FormHelperText>
                                            {fieldState.error?.message || "Ative esta opção para permitir que este guichê seja exibido como disponível."}
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Grid>
                    </CardContent>
                    <CardActions className={classes.cardActions}>
                        <Button
                            className={classes.button}
                            onClick={() => setOpen(false)}
                            disabled={mutation.isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            className={defaultClasses.addButton}
                            disabled={mutation.isLoading}
                        >
                            {mutation.isLoading ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </CardActions>
                </Card>
            </Dialog >
        </>
    );
}
